import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * What a tab needs the page header to render for it.
 *
 * Deliberately the same shape `EditActions` takes, rather than a React node: the point is that a
 * tab's Save sits where every other Save on these pages sits and looks identical, and handing over a
 * node would let one drift.
 */
export interface TabEditSession {
	saving: boolean;
	onSave: () => void;
	onCancel: () => void;
	saveLabel?: string;
	savingLabel?: string;
	cancelLabel?: string;
}

interface TabEditSessionRegistry {
	session: TabEditSession | null;
	register: (session: TabEditSession | null) => void;
}

const Context = createContext<TabEditSessionRegistry | null>(null);

/**
 * Lets a tab body publish its unsaved-changes state to the page header above it.
 *
 * <p>The account pages keep Save/Cancel in the sticky header on desktop and in a fixed bar on mobile,
 * driven by the page's own edit mode. A tab that manages its own dirty state — communication
 * preferences is the first — has nothing to hang off that, and a second pair of buttons halfway down
 * the page is the thing this avoids: two Saves on one screen, only one of which applies to what the
 * customer is looking at.</p>
 *
 * <p>A context rather than props because the tab body is rendered several levels below the shell, and
 * threading a callback through every page's tab switch would put the plumbing in the pages that are
 * meant to stay thin.</p>
 */
export function TabEditSessionProvider({ children }: { children: React.ReactNode }) {
	const [session, register] = useState<TabEditSession | null>(null);

	const value = useMemo(() => ({ session, register }), [session]);

	return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Read by the shell. Null outside a provider, so the shell works unwrapped. */
export function useTabEditSession(): TabEditSession | null {
	return useContext(Context)?.session ?? null;
}

/**
 * Publish this tab's edit session, or `null` while there is nothing to save.
 *
 * <p>Cleared on unmount, so switching tabs mid-edit does not leave a Save in the header that would
 * act on a screen nobody is looking at.</p>
 *
 * <p>The callbacks are held in a ref and the registered ones are stable wrappers around it. Passing
 * the caller's own closures through would re-register on every render — they are rebuilt each time —
 * and since registering sets state in the provider above, that is a render loop rather than merely
 * wasteful. Only the primitives are dependencies.</p>
 */
export function usePublishTabEditSession(session: TabEditSession | null) {
	const registry = useContext(Context);
	const register = registry?.register;

	const latest = useRef(session);
	latest.current = session;

	const active = session != null;
	const saving = session?.saving ?? false;
	const { saveLabel, savingLabel, cancelLabel } = session ?? {};

	useEffect(() => {
		if (!register) {
			return undefined;
		}

		if (!active) {
			register(null);
			return undefined;
		}

		register({
			saving,
			saveLabel,
			savingLabel,
			cancelLabel,
			onSave: () => latest.current?.onSave(),
			onCancel: () => latest.current?.onCancel(),
		});

		return () => register(null);
	}, [register, active, saving, saveLabel, savingLabel, cancelLabel]);
}
